import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Mail, Save, Loader2, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPOSES = [
  { v: 'digest', label: 'Email Digest', desc: 'Used to send scheduled PT/clinic reports.' },
  { v: 'auth',   label: 'Password Reset', desc: 'Used ONLY for forgot-password + admin-reset emails. Falls back to Digest if unset.' },
];

/** Settings → SMTP Configs (super_admin only). Manages digest + auth SMTP separately. */
export default function SmtpConfigs() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/smtp-configs`);
      const byPurpose = {};
      (r.data?.configs || []).forEach(c => { byPurpose[c.purpose || 'digest'] = c; });
      setConfigs(byPurpose);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const update = (purpose, field, val) => {
    setConfigs(prev => ({ ...prev, [purpose]: { ...(prev[purpose] || { purpose }), [field]: val } }));
  };

  const save = async (purpose) => {
    const c = configs[purpose] || {};
    if (!c.host || !c.email || !c.password || !c.port) {
      toast.error('Host, port, email, and password are required'); return;
    }
    setSaving(purpose);
    try {
      await axios.post(`${API}/auth/smtp-configs`, {
        purpose, host: c.host, port: Number(c.port) || 587,
        email: c.email, password: c.password,
        use_tls: c.use_tls !== false,
        sender_email: c.sender_email || null,
        sender_name: c.sender_name || null,
      });
      toast.success(`${purpose.toUpperCase()} SMTP saved`);
      // Password field is redacted on read; clear our local copy for hygiene
      setConfigs(prev => ({ ...prev, [purpose]: { ...prev[purpose], password: '' } }));
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-4" data-testid="smtp-configs-page">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold">Email SMTP Configuration</h2>
      </div>
      <p className="text-sm text-slate-600">
        Configure separate mailboxes for report digests and auth (password-reset) emails.
      </p>
      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : PURPOSES.map(p => {
        const c = configs[p.v] || {};
        return (
          <Card key={p.v}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold flex items-center gap-1.5">
                    {p.v === 'auth' ? <Lock className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    {p.label}
                    {c.host ? <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">Configured</span>
                            : <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">Not configured</span>}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">{p.desc}</p>
                </div>
                <Button onClick={() => save(p.v)} disabled={saving === p.v} data-testid={`smtp-save-${p.v}`}>
                  {saving === p.v ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>Host</Label><Input value={c.host || ''} onChange={e => update(p.v, 'host', e.target.value)} placeholder="smtp.gmail.com" data-testid={`smtp-host-${p.v}`} /></div>
                <div><Label>Port</Label><Input type="number" value={c.port || 587} onChange={e => update(p.v, 'port', e.target.value)} /></div>
                <div><Label>Email</Label><Input value={c.email || ''} onChange={e => update(p.v, 'email', e.target.value)} placeholder="you@company.com" /></div>
                <div><Label>Password / App Key</Label><Input type="password" value={c.password || ''} onChange={e => update(p.v, 'password', e.target.value)} placeholder={c.host ? '(unchanged — enter to update)' : ''} /></div>
                <div><Label>Sender Email (optional)</Label><Input value={c.sender_email || ''} onChange={e => update(p.v, 'sender_email', e.target.value)} placeholder="Same as Email if blank" /></div>
                <div><Label>Sender Name (optional)</Label><Input value={c.sender_name || ''} onChange={e => update(p.v, 'sender_name', e.target.value)} placeholder="OPTSC Reporting" /></div>
                <div className="col-span-2 flex items-center gap-2 mt-4">
                  <Switch checked={c.use_tls !== false} onCheckedChange={v => update(p.v, 'use_tls', v)} id={`tls-${p.v}`} />
                  <Label htmlFor={`tls-${p.v}`} className="cursor-pointer">Use STARTTLS (recommended)</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
